import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import maplibregl, { Map } from 'maplibre-gl';
import Router from 'next/router';
import { useMap } from '@/hooks/useMap';
import { formatNumber, getTotalKm, sanitizeRegion } from '@/utils/explorer';
import { IRegion } from '@/types/explorer';
import * as CoverageData from '@/components/ui/map/sources/CoverageData';
import * as NewCoverageData from '@/components/ui/map/sources/NewCoverageData';
import * as RegionLabels from '@/components/ui/map/sources/RegionLabels';
import * as Regions from '@/components/ui/map/layers/Regions';
import { addLayer as addCoverageLayer } from '@/components/ui/map/layers/Coverage';

interface Props {
  children?: React.ReactNode;
  region?: IRegion;
  regions?: IRegion[];
}

function RegionMap({ region, regions }: Props) {
  const mapMounted = useRef(false);
  const { map, setAreLayersReady, mapContainerRef } = useMap();

  const MarkerContent = ({ region }: { region: IRegion }) => {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-neutral-900 p-2 md:p-4">
        <div className="text-2xs font-bold md:text-base">{region.name}</div>
        <div className="flex gap-2 text-2xs md:gap-8 md:text-[10px]">
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Total Road Km Mapped</span>
            <span>
              {formatNumber(getTotalKm(region.totalCoveredL12Cells ?? 0))}
            </span>
          </div>
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Unique Road Km Mapped</span>
            <span>
              {formatNumber(getTotalKm(region.uniqueCoveredL12Cells ?? 0))}
            </span>
          </div>
          <div className="flex flex-col leading-normal">
            <span className="font-bold uppercase">Contributors</span>
            <span>{formatNumber(region.totalUsers ?? 0)}</span>
          </div>
        </div>
      </div>
    );
  };

  const locateRegion = useCallback(
    (map: maplibregl.Map, region: IRegion | undefined) => {
      if (!map || !region) {
        return;
      }

      const { lat, lon } = region.focalPoint || {};
      if (!lat || !lon) {
        return;
      }

      const markerDiv = document.createElement('div');
      markerDiv.className = 'marker';

      const root = createRoot(markerDiv);
      root.render(<MarkerContent region={region} />);

      new maplibregl.Marker({ element: markerDiv })
        .setLngLat([lon, lat])
        .addTo(map);

      return () => {
        root.unmount();
        map.remove();
      };
    },
    [],
  );

  const initializeData = useCallback(
    async (
      map: Map,
      regionId: string,
      regions: IRegion[],
      setAreLayersReady: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setAreLayersReady(false);

      // Register Sources
      await CoverageData.addSource(map);
      await NewCoverageData.addSource(map);
      await RegionLabels.addSource(map, regions);
      addCoverageLayer(map);
      Regions.addLayer({
        map,
        selectedRegionId: regionId,
        shouldHighlightOnHover: false,
      });
      setAreLayersReady(true);
    },
    [],
  );

  useEffect(() => {
    if (map && !mapMounted.current && region && regions) {
      initializeData(map, region.id, regions, setAreLayersReady);
      locateRegion(map, region);
      mapMounted.current = true;
    }
  }, [map, initializeData, setAreLayersReady, regions, region, locateRegion]);

  const bbox: any = region?.bbox;

  useEffect(() => {
    if (map && bbox) {
      const [minLng, minLat, maxLng, maxLat] = bbox;

      const bounds: any = [
        [minLng, minLat],
        [maxLng, maxLat],
      ];

      map.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        animate: false,
      });
    }
  }, [map, bbox]);

  return (
    <>
      <div className="relative size-full overflow-hidden rounded-lg [&_canvas]:h-full">
        <div ref={mapContainerRef} className="size-full" />
      </div>
    </>
  );
}

export default RegionMap;
